import { useEffect, useCallback, useState, useMemo, memo } from "react";
import CardTable from "../../components/common/CardTable";
import { useAppSelector, useAppDispatch } from "../../store/hooks";
import type { RootState } from "../../store/store";
import { formatDateOnlyMDY } from "../../utils/date";
import ErrorBanner, {
  ValidationError,
} from "../../components/common/ErrorBanner";
import {
  PerformanceData as PerformanceTableData,
  performanceColumns,
  setPerformanceData,
  setInitialPerformanceData,
  updatePerformanceData,
  setHasUnsavedChanges,
} from "../../store/slices/pathToPerformanceSlice";
import EditableTable, {
  EditableTableRow,
} from "../../components/common/EditableTable";
import { 
  HUDDLES_OPTIONS,
} from "../../constants/pathToPerformance";
import { toString } from "../../utils/typeGuards";

/**
 * Props for the PerformanceTable component
 */
interface PerformanceTableProps {
  /** Performance data array to display in the table */
  data: PerformanceTableData[];
  /** Whether the table should be read-only (no editing allowed) */
  readOnly?: boolean;
  /** Inline date validation error for the Last Face-to-Face Assessment field (from page-level save). */
  dateError?: string | null;
  /** Called when the user edits the Last Face-to-Face Assessment field, so the parent can clear its dateError state. */
  onDateFieldChange?: () => void;
}

const getPerformanceFieldValues = (row: EditableTableRow) => ({
  id: toString(row.id, "1"),
  usingHuddles: toString(row.usingHuddles, HUDDLES_OPTIONS.NO),
  lastFaceToFaceAssessment: toString(row.lastFaceToFaceAssessment, ""),
});

const updatePerformanceRow = (
  rows: PerformanceTableData[],
  rowId: string,
  usingHuddles: string,
  lastFaceToFaceAssessment: string,
): PerformanceTableData[] => rows.map((item) =>
  item.id === rowId
    ? { ...item, usingHuddles, lastFaceToFaceAssessment }
    : item,
);

const hasPerformanceChanges = (
  rows: PerformanceTableData[],
  initialRows: PerformanceTableData[],
): boolean => {
  const current = rows[0];
  const initial = initialRows[0];
  return Boolean(
    current && initial && (
      current.usingHuddles !== initial.usingHuddles ||
      current.lastFaceToFaceAssessment !== initial.lastFaceToFaceAssessment
    ),
  );
};

/**
 * PerformanceTable Component
 * 
 * Displays and manages UBT team performance data including:
 * - Team name, service type, and ratings
 * - Huddles usage status  
 * - Team establishment and closure dates
 * - Last face-to-face assessment dates
 * 
 * Features:
 * - Editable fields for huddles and assessment dates
 * - Real-time validation of date formats
 * - Save/reset functionality with change detection
 * - Integration with Redux for state management
 * - Optimized with React.memo for performance
 * 
 * @ Transforms API data format to table-friendly format with date formatting and huddles transformation
   *param props - Component props
 * @returns Rendered performance table with edit capabilities
 */
const PerformanceTable = memo<PerformanceTableProps>(({
  data,
  readOnly = false,
  dateError,
  onDateFieldChange,
}) => {
  const dispatch = useAppDispatch();
  const { teamData, p2pData, initialPerformanceData, performanceData } = useAppSelector(
    (state: RootState) => state.pathToPerformance,
  );
  
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>([]);

  // Use performanceData from Redux state, fallback to prop if not available
  const tableData = performanceData.length > 0 ? performanceData : data;

  /**
   * Map team aggregated data to performance table when data is loaded
   */
  useEffect(() => {
    if (teamData && p2pData) {
      const mappedPerformanceData: PerformanceTableData[] = [
        {
          id: teamData.teamId.toString(),
          ubtName: teamData.teamName || "",
          serviceType: teamData.serviceDeliveryType || "",
          usingHuddles: teamData.usingHuddles === HUDDLES_OPTIONS.API_YES ? HUDDLES_OPTIONS.YES : HUDDLES_OPTIONS.NO,
          dateTeamEstablished: teamData.teamDateCreated
            ? formatDateOnlyMDY(teamData.teamDateCreated)
            : "",
          dateTeamClosed: "",
          lastFaceToFaceAssessment: p2pData.current?.learningPlanRefreshDate
            ? formatDateOnlyMDY(p2pData.current.learningPlanRefreshDate)
            : "",
          teamRating: teamData.teamRating || "",
        },
      ];

      dispatch(setPerformanceData(mappedPerformanceData));
      dispatch(setInitialPerformanceData(mappedPerformanceData));
    }
  }, [teamData, p2pData, dispatch]);

  /**
   * Handle rows change from EditableTable when user edits fields
   * 
   * Updates Redux state with edited data and manages change tracking:
   * - Extracts string values from ReactNode using type guards
   * - Clears validation errors when editing the error field
   * - Updates performance data in Redux
   * - Sets hasUnsavedChanges flag only if values differ from initial data
   * 
   * @param updatedRows - Array of updated rows from EditableTable
   */
  const handleRowsChange = useCallback((updatedRows: EditableTableRow[]) => {
    const updatedRow = updatedRows[0];
    if (!updatedRow) return;

    const { id, usingHuddles: huddlesValue, lastFaceToFaceAssessment: assessmentValue } =
      getPerformanceFieldValues(updatedRow);
      
    if (assessmentValue !== (tableData[0]?.lastFaceToFaceAssessment || "")) {
      setValidationErrors([]);
      onDateFieldChange?.();
    }
      
    dispatch(updatePerformanceData({
      id,
      data: {
        usingHuddles: huddlesValue,
        lastFaceToFaceAssessment: assessmentValue,
      },
    }));
      
    const updatedPerformanceData = updatePerformanceRow(
      tableData,
      id,
      huddlesValue,
      assessmentValue,
    );
    const hasActualChanges = hasPerformanceChanges(
      updatedPerformanceData,
      initialPerformanceData,
    );
      
    dispatch(setHasUnsavedChanges(hasActualChanges));

  }, [dispatch, tableData, initialPerformanceData, onDateFieldChange]);

  /**
   * Sync external date error (from page-level save) into local validation errors.
   * Clears automatically when the user edits the field.
   */
  useEffect(() => {
    if (dateError) {
      setValidationErrors([{ field: "lastFaceToFaceAssessment", message: dateError }]);
    } else {
      setValidationErrors([]);
    }
  }, [dateError]);

  /**
   * Inject validation errors into the column definitions so EditableTable
   * can render inline error messages beneath the affected cell.
   */
  const columnsWithErrors = useMemo(
    () =>
      performanceColumns.map((col) => {
        if (col.key === "lastFaceToFaceAssessment") {
          const colError = validationErrors.find(
            (e) => e.field === "lastFaceToFaceAssessment",
          );
          return { ...col, error: colError?.message };
        }
        return col;
      }),
    [validationErrors],
  );

  const rows: EditableTableRow[] = tableData.map((row) => ({
    id: row.id,
    ubtName: <span className="whitespace-normal break-all">{row.ubtName}</span>,
    serviceType: row.serviceType,
    usingHuddles: row.usingHuddles,
    dateTeamEstablished: row.dateTeamEstablished,
    dateTeamClosed: row.dateTeamClosed,
    lastFaceToFaceAssessment: row.lastFaceToFaceAssessment,
    teamRating: row.teamRating,
  }));

  return (
    <div className="w-full">
      {validationErrors.length > 0 && (
        <div className="mb-8!">
          <ErrorBanner errors={validationErrors} />
        </div>
      )}
      <CardTable>
        {rows.length > 0 ? (
          <EditableTable
            columns={columnsWithErrors}
            rows={rows}
            isEditMode={!readOnly}
            onRowsChange={handleRowsChange}
          />
        ) : (
          <p className="p3 -book text-center mt-2.5! desktop:mt-50!">
            No assessment data available.
          </p>
        )}
      </CardTable>
    </div>
  );
});

PerformanceTable.displayName = "PerformanceTable";

export default PerformanceTable;
